import { useEffect, useState } from "react";
import Table from "../../../components/Table";
import MaterialTypography from "../../../components/MaterialTypography";
import { Card, CardHeader, Stack } from "@mui/material";
import { useNavigate } from "react-router-dom";
import MaterialButton from "../../../components/MaterialButton";
import AddIcon from "@mui/icons-material/Add";
import { FileUpload, FilterAlt } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import {
  setCurrentPage,
  setRowsPerPage,
  setTotalPages,
} from "./store/motorVariantStore";
import { deleteData, fetchData } from "../../../services";
import { useLoading } from "../../../components/Loading";
import BulkUpload from "../../../components/blukUplod";
import { useSnackbar } from "../../../context/popup";
import MotorVariantListFilter from "./components/MotorVariantListFilter.js";

const MotorVariantList = ({tabState}) => {

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { showSnackbar } = useSnackbar();
  const { setCircleLoading } = useLoading();

  const currentPage = useSelector(
    (state) => state?.motorVariantStore?.currentPage
  );
  const rowsPerPage = useSelector(
    (state) => state?.motorVariantStore?.rowsPerPage
  );
  const totalPages = useSelector(
    (state) => state?.motorVariantStore?.totalPages
  );

  const [search, setSearch] = useState("");
  const [isRefresh, setIsRefresh] = useState(false);
  const [openUploadFileDialog, setOpenUploadFileDialog] = useState(false);
  const [variantData, setVariantData] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const [filterData, setFilterData] = useState({
    isActive: "",
    motorCategoryId: "",
    motorModelId: "",
    fuelTypeId: ""
  });
  
  useEffect(() => {
    if(tabState === "variant"){
      fetchVariantData();
    }
  }, [isRefresh, currentPage, rowsPerPage, search]);

  //fetch variant list api
  const fetchVariantData = async () => {
    setCircleLoading(true);
    try {
      let url = `/reference/motor/mmv/variants?page=${currentPage}&limit=${rowsPerPage}&searchTerm=${search}&motorCategoryId=${filterData?.motorCategoryId}&motorModelId=${filterData?.motorModelId}&fuelTypeId=${filterData?.fuelTypeId}`;
            
      //for active and inactive status
      if (filterData.isActive !== "") {
        url += `&isActive=${filterData.isActive}`;
      }

      const response = await fetchData(url);

      const successResponse = response?.success;
      if (successResponse) {
        setVariantData(response?.data?.items);
        dispatch(setTotalPages(response?.data?.totalPages));
      } else {
        showSnackbar("Failed to fetch data", "error");
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      showSnackbar(error?.response?.data?.message || 'Network Error', "error");
    } finally {
      setCircleLoading(false);
    }
  };

  const handleDelete = async (row) => {
    try {
      const response = await deleteData(`/reference/motor/mmv/variant-delete`, {
        ids: [row?.id],
      });
      setIsRefresh(!isRefresh);
      const successResponse = response?.success;
      const responseMessage = response?.message;
      if (successResponse) {
        showSnackbar(responseMessage, "success");
      } else {
        showSnackbar(responseMessage, "error");
      }
    } catch (error) {
      console.error("Error deleting data:", error);
      showSnackbar("Failed to delete Motor Variant", "error");
    }
  };

  const columns = [
    {
      key: "sno",
      label: "S.No.",
      renderCell: (row) => row.sno,
    },
    {
      key: "name",
      label: "Variant Name",
      renderCell: (row) => row.name,
    },
    {
      key: "normalizedMmv",
      label: "MMV",
      renderCell: (row) => row?.normalizedMmv,
    },
    {
      key: "motorCategory",
      label: "Motor Category",
      renderCell: (row) => row.motorCategory.keyName,
    },
    {
      key: "fuelType",
      label: "Fuel Type",
      renderCell: (row) => row.fuelType?.keyName,
    },
    {
      key: "bodyType",
      label: "Body Type",
      renderCell: (row) => row.bodyType?.name,
    },
    {
      key: "bodyStyle",
      label: "Body Style",
      renderCell: (row) => row.bodyStyle?.name,
    },
    {
      key: "isActive",
      label: "Status",
      renderCell: (row) => (
        <span>
          {row.isActive ? (
            <span style={{ color: "green", fontWeight: "bold" }}>Active</span>
          ) : (
            <span style={{ color: "red", fontWeight: "bold" }}>Inactive</span>
          )}
        </span>
      ),
    },
  ];

  const rows = variantData.map((item, index) => ({
    ...item,
    sno: index + 1,
  }));

  const handlePaginationChange = (page) => {
    dispatch(setCurrentPage(page));
  };

  const handleRowsPerPageChange = (rowsPerPage) => {
    dispatch(setRowsPerPage(rowsPerPage));
    dispatch(setCurrentPage(1));
  };

  const handleEdit = (row) => {
    navigate(`/motor-master/edit-variant/${row?.id}`);
  };

  const handleView = (row) => {
    navigate(`/motor-master/view-variant/${row?.id}`, {
      state: { isView: true },
    });
  };

  const handleSearch = (value) => {
    setSearch(value);
  };

  const actions = {
    edit: true,
    view: true,
    delete: true,
    onEdit: (row) => handleEdit(row),
    onView: (row) => handleView(row),
    onDelete: (row) => handleDelete(row),
  };

  const handleAddNewVariant = () => {
    navigate("/motor-master/add-variant");
  };

  return (
    <div>
      {/* <DashboardLayout> */}
      <CardHeader
        title={
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={2}
          >
            <Stack direction="column" spacing={0}>
              <MaterialTypography variant="h5" fontWeight="medium">
                Motor Variant management list
              </MaterialTypography>
              <MaterialTypography variant="caption" color="text">
                View and manage details of all motor variants.
              </MaterialTypography>
            </Stack>

            <Stack direction="row" spacing={1}>
              <MaterialButton
                variant="gradient"
                color="dark"
                size="small"
                startIcon={<FilterAlt />}
                onClick={(event) => setAnchorEl(event.currentTarget)}
              >
                Filter
              </MaterialButton>
              <MaterialButton
                variant="gradient"
                color="dark"
                size="small"
                startIcon={<FileUpload />}
                onClick={() => setOpenUploadFileDialog(true)}
              >
                Bulk Upload
              </MaterialButton>
              <MaterialButton
                variant="gradient"
                color="dark"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => handleAddNewVariant()}
              >
                Add Variant
              </MaterialButton>
            </Stack>
          </Stack>
        }
      />
      <Card sx={{ mx: 1 }}>
        <Table
          columns={columns}
          data={rows}
          actions={actions}
          pagination={{
            totalPages,
            currentPage,
            onPageChange: (page) => handlePaginationChange(page),
            rowsPerPage,
            onRowsPerPageChange: (rowsPerPage) =>
              handleRowsPerPageChange(rowsPerPage),
            rowsPerPageOptions: [5, 10, 15, 20],
          }}
          getRowKey={(row) => row.id}
          className="custom-table"
          handleSearch={handleSearch}
          noDataLabel={"No Motor Variant data found"}
        />
      </Card>
      <BulkUpload
        open={openUploadFileDialog}
        setOpen={setOpenUploadFileDialog}
        endPoint="/reference/master/bulkUpload/motor-variant"
        fetchList={fetchVariantData}
      />      
      <MotorVariantListFilter
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        filterData={filterData}
        setFilterData={setFilterData}
        onApply={() => {
          setAnchorEl(null); // close popup
          fetchVariantData(); // call API after filter applied
        }}
      />
      {/* </DashboardLayout> */}
    </div>
  );
};

export default MotorVariantList;
